import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { ViewScoreSmall } from "../../../../../coding/views/ViewScoreSmall";
import { LifeCycleStageControls } from "./LifeCycleStageControls";
import { DesignStageControls } from "./DesignStageControls";
import { UploadNotFinishedAlert } from "./UploadNotFinishedAlert";
import { CreateOrUpdateOsaWayModal } from "../../../../../osaway/views/CreateOrUpdateRoute/CreateOrUpdateOsaWayModal";
import { DeleteOsaWayModal } from "../../../../../osaway/views/DeleteOsaWayModal/DeleteOsaWayModal";
import { SelectFirstNodeThumbnail } from "./SelectFirstNodeThumbnail";
import { OsaWayStats } from "./OsaWayStats";
import { LifeCycleStage } from "../../../../../osaway/models/LifeCycleStage";
import { useStores } from "../../../../../shared/stores/RootStore";
import {
  ArrowBackIcon,
  DeleteIcon,
  EditIcon,
} from "../../../../../shared/views/icons";
import { PopulateWayControls } from "./PopulateWayControls";

const i18prefix = "map:routeSummary";

const OsaWayDetailBase: React.FC = () => {
  const { mapStore, authStore, osaNodeStore, osaWayStore } = useStores();
  const wayEditingModal = useDisclosure();
  const wayDeletingModal = useDisclosure();

  const osaWay = osaWayStore.getSelected()!;
  const nodes = osaNodeStore.nodesByWayId.get(osaWay.id);
  const firstNode = nodes?.[0];

  const refreshOsaWay = async () => {
    Promise.all([
      osaWayStore.refreshOsaWay(osaWay.id),
      osaNodeStore.fetchNodes(osaWay.id),
    ]);
  };

  const selectUnprocessedNodes = () => {
    const multiSelectedIds = new Set<string>();
    nodes?.forEach((node) => {
      if (node.photo === null) {
        multiSelectedIds.add(node.id);
      }
    });
    osaNodeStore.setMultiSelectedIds(multiSelectedIds);
  };

  return (
    <Stack py={2} gap={4} overflowY={"auto"}>
      <HStack px={2} mb={-2}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={t(`${i18prefix}.clearSelection`)}
            onClick={() => {
              osaWayStore.clearSelected();
              mapStore.recenterMap();
            }}
            title={t(`${i18prefix}.clearSelection`)}
          >
            <Icon as={ArrowBackIcon} />
          </IconButton>
        </Box>
        <Text flexGrow={1} px={2} fontWeight="semibold" textAlign="center">
          {osaWay.name}
        </Text>
        <Box flexShrink={0} width="40px" />
      </HStack>

      {authStore.isAuthenticated && (
        <ButtonGroup
          alignSelf="center"
          colorPalette="primary"
          variant="outline"
          size="sm"
          attached
        >
          <IconButton
            aria-label={t(`${i18prefix}.edit`)}
            title={t(`${i18prefix}.edit`)}
            disabled={!osaWay.permissions.canUpdate}
            onClick={wayEditingModal.onOpen}
          >
            <EditIcon />
          </IconButton>

          <IconButton
            aria-label={t(`${i18prefix}.delete`)}
            title={t(`${i18prefix}.delete`)}
            disabled={!osaWay.permissions.canDelete}
            onClick={wayDeletingModal.onOpen}
          >
            <DeleteIcon />
          </IconButton>
        </ButtonGroup>
      )}

      {authStore.isAuthenticated && (
        <LifeCycleStageControls osaWay={osaWay} size="sm" />
      )}

      <Stack px={4} gap={4}>
        <OsaWayStats />

        {osaWay.lifeCycleStage === LifeCycleStage.Design &&
          osaWay.permissions.canUpdate && <DesignStageControls />}

        {osaWay.score && (
          <HStack py={2} justifyContent={"center"}>
            <ViewScoreSmall score={osaWay.score} />
          </HStack>
        )}

        {osaWay.lifeCycleStage === LifeCycleStage.DataCollection &&
          !osaWay.uploadFinished && (
            <UploadNotFinishedAlert osaWayId={osaWay.id}>
              <Button size="sm" colorPalette="primary" onClick={refreshOsaWay}>
                {t("osaway:photos.refresh")}
              </Button>
              <Button
                size="sm"
                colorPalette="primary"
                onClick={selectUnprocessedNodes}
              >
                {t("osaway:photos.selectUnprocessedNodes")}
              </Button>
            </UploadNotFinishedAlert>
          )}

        {osaWay.lifeCycleStage === LifeCycleStage.DataCollection &&
          !nodes?.length && <PopulateWayControls />}

        {osaWay.lifeCycleStage !== LifeCycleStage.Coding &&
          osaWay.lifeCycleStage !== LifeCycleStage.Design &&
          !!firstNode?.photo && (
            <SelectFirstNodeThumbnail
              firstNode={firstNode}
              selectNodeById={(nodeId) => {
                osaNodeStore.select(nodeId);
                mapStore.recenterMap();
              }}
            />
          )}
      </Stack>

      {wayEditingModal.open && (
        <CreateOrUpdateOsaWayModal
          onClose={wayEditingModal.onClose}
          osaWay={osaWay}
        />
      )}

      {wayDeletingModal.open && (
        <DeleteOsaWayModal onClose={wayDeletingModal.onClose} />
      )}
    </Stack>
  );
};

export const OsaWayDetail = observer(OsaWayDetailBase);
